import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  SLOT_STATUS_LABEL_KEY,
  SLOT_STATUS_TONE,
  type SlotStatus,
} from '@/lib/slot-status';
import { TONES } from '@/lib/tones';
import { cn } from '@/lib/utils';

/**
 * Header of a slot detail sheet: the status pill on the left, then the slot's
 * owner and its schedule. Extra nodes (the ID / last-modified row) stack below.
 *
 * @param props.status - the slot status, driving the pill tone and label
 * @param props.title - the attending doctor the slot belongs to
 * @param props.dateLabel - the day the slot happens on
 * @param props.timeLabel - the time range, when the slot has one
 * @param props.children - additional header rows rendered under the title
 * @returns the sheet header element
 */
export const SlotSheetHeader = ({
  status,
  title,
  dateLabel,
  timeLabel,
  children,
}: {
  status: SlotStatus;
  title: ReactNode;
  dateLabel: string;
  timeLabel: string | null;
  children?: ReactNode;
}) => {
  const { t } = useTranslation();
  const tone = TONES[SLOT_STATUS_TONE[status]];

  return (
    <SheetHeader className="gap-3 border-b p-6 pr-12">
      <div className="flex items-start gap-3">
        <Badge
          variant="outline"
          className={cn(
            'shrink-0 gap-1.5 border-transparent',
            tone.pill,
            tone.text,
          )}
        >
          <span
            className={cn('size-1.5 rounded-full', tone.dot)}
            aria-hidden="true"
          />
          {t(SLOT_STATUS_LABEL_KEY[status])}
        </Badge>

        <div className="min-w-0 flex-1 space-y-1">
          <SheetTitle className="text-base leading-tight">{title}</SheetTitle>
          <SheetDescription className="text-xs">
            {dateLabel}
            {timeLabel && <span> · {timeLabel}</span>}
          </SheetDescription>
        </div>
      </div>

      {children}
    </SheetHeader>
  );
};
